const FIRST_TOUCH_STORAGE_KEY = "reelrewards:first-touch-attribution";

type FirstTouchPayload = {
	utmSource?: string;
	utmCampaign?: string;
	utmMedium?: string;
	landingPath?: string;
	fbc?: string;
	fbp?: string;
};

export function captureFirstTouchAttribution() {
	if (typeof window === "undefined") return;

	const existing = readStoredFirstTouch();
	if (existing) return;

	const searchParams = new URLSearchParams(window.location.search);
	const utmSource = clean(searchParams.get("utm_source"));
	const utmCampaign = clean(searchParams.get("utm_campaign"));
	const utmMedium = clean(searchParams.get("utm_medium"));
	const fbc = getFbcFromBrowser(searchParams);
	const fbp = clean(readCookie("_fbp"));

	if (!utmSource && !utmCampaign && !utmMedium && !fbc && !fbp) return;

	const payload: FirstTouchPayload = {
		utmSource,
		utmCampaign,
		utmMedium,
		landingPath: clean(
			`${window.location.pathname}${window.location.search || ""}`
		),
		fbc,
		fbp,
	};

	window.localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(payload));
}

export async function submitFirstTouchAttribution(accessToken: string) {
	if (typeof window === "undefined" || !accessToken) return;

	const payload = readStoredFirstTouch();
	if (!payload) return;

	const baseUrl = import.meta.env.VITE_SERVER_API_URL;
	if (!baseUrl) return;

	try {
		const searchParams = new URLSearchParams(window.location.search);
		const fbc = payload.fbc ?? getFbcFromBrowser(searchParams);
		const fbp = payload.fbp ?? clean(readCookie("_fbp"));

		const response = await fetch(baseUrl + "/auth/attribution/first-touch", {
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				utm_source: payload.utmSource,
				utm_campaign: payload.utmCampaign,
				utm_medium: payload.utmMedium,
				landing_path: payload.landingPath,
				fbc,
				fbp,
			}),
		});

		if (response.ok) {
			window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
		}
	} catch {
		// Best-effort tracking. Fail silently to avoid affecting auth UX.
	}
}

export function getMetaAttributionValues() {
	if (typeof window === "undefined") {
		return { fbc: undefined as string | undefined, fbp: undefined as string | undefined };
	}

	const payload = readStoredFirstTouch();
	const searchParams = new URLSearchParams(window.location.search);

	const fbc = payload?.fbc ?? getFbcFromBrowser(searchParams);
	const fbp = payload?.fbp ?? clean(readCookie("_fbp"));

	return { fbc, fbp };
}

function readStoredFirstTouch(): FirstTouchPayload | null {
	try {
		const raw = window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as FirstTouchPayload;
			if (
				!parsed?.utmSource &&
				!parsed?.utmCampaign &&
				!parsed?.utmMedium &&
				!parsed?.fbc &&
				!parsed?.fbp
			) {
				return null;
			}
		return parsed;
	} catch {
		return null;
	}
}

function clean(value: string | null | undefined) {
	if (!value) return undefined;
	const normalized = value.trim();
	return normalized ? normalized : undefined;
}

function readCookie(name: string) {
	if (typeof document === "undefined") return undefined;
	const cookie = document.cookie
		.split("; ")
		.find((row) => row.startsWith(`${name}=`));
	if (!cookie) return undefined;
	const value = cookie.slice(name.length + 1);
	return decodeURIComponent(value);
}

function getFbcFromBrowser(searchParams: URLSearchParams) {
	const fbcFromCookie = clean(readCookie("_fbc"));
	if (fbcFromCookie) return fbcFromCookie;

	const fbclid = clean(searchParams.get("fbclid"));
	if (!fbclid) return undefined;

	return `fb.1.${Date.now()}.${fbclid}`;
}
